import { Check, ChevronDown, ChevronUp, ExternalLink } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useModal } from 'react-modal-hook';
import { useReducer, useState } from 'react';

import { GetMollieConnectionsQuery as GetMollieConnections, MollieAppPricingType } from '__generated__/graphql';

import { InfoTooltip } from '../../common/UI/InfoTooltip';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import DisableMolliePaymentMethodMutation from './DisableMolliePaymentMethodMutation';
import EnableMolliePaymentMethodMutation from './EnableMolliePaymentMethodMutation';
import SetDefaultPaymentProfileMutation from './SetDefaultPaymentProfileMutation';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';

type MollieConnection = GetMollieConnections['project']['mollie_connections'][0];
type MollieProfile = GetMollieConnections['project']['mollie_connections'][0]['profiles'][0];
type MolliePaymentMethod = MollieProfile['payment_methods'][0];

export interface MollieProfileSectionProps {
  connection: MollieConnection;
  profile: MollieProfile;
}

const MollieProfileSection = ({ connection, profile }: MollieProfileSectionProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const [setDefaultPaymentProfile, { loading }] = (
    useMutation(SetDefaultPaymentProfileMutation, {
      variables: {
        id: profile.payment_profile.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.default_profile_changed'));
      },
    })
  );

  const [showingSettings, togglePaymentMethods] = useReducer((showing) => !showing, false);
  const [confirmDefault, toggleConfirmDefault] = useReducer((confirm) => !confirm, false);

  return (
    <UI.GridContainer>
      <UI.Div>
        <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
          <UI.Div>
            <UI.H4>
              {profile.name}
            </UI.H4>
            <UI.Delimit>
              {profile.website}
              {profile.status !== 'verified' && (
                t(`mollie_statuses.${profile.status}`)
              )}
            </UI.Delimit>
          </UI.Div>
          <UI.Div sc={{ noWrap: true }}>
            {profile.payment_profile.default && (
              <UI.Span sc={{ muted: true, mr: 3 }}>
                <UI.Icon>
                  <Check />
                </UI.Icon>
                {' '}
                {t('default')}
              </UI.Span>
            )}
            <UI.Button
              onClick={() => togglePaymentMethods()}
              sc={{ size: 'sm', active: showingSettings, my: -1 }}
            >
              {t('settings')}
              {' '}
              <UI.Icon>
                {!showingSettings && <ChevronDown />}
                {showingSettings && <ChevronUp />}
              </UI.Icon>
            </UI.Button>
          </UI.Div>
        </UI.FlexContainer>
      </UI.Div>

      {showingSettings && (
        <>
          {!profile.payment_profile.default && (
            <>
              <UI.HR />
              <UI.Form onSubmit={() => setDefaultPaymentProfile()}>
                <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <UI.Checkbox
                    checked={confirmDefault}
                    onChange={() => toggleConfirmDefault()}
                  >
                    {t('use_as_default_payment_profile')}
                  </UI.Checkbox>
                  <UI.Button
                    type="submit"
                    sc={{ brand: 'secondary', loading, size: 'sm' }}
                    disabled={!confirmDefault || profile.payment_profile.default || loading}
                  >
                    {t('confirm')}
                  </UI.Button>
                </UI.FlexContainer>
              </UI.Form>
            </>
          )}
          {profile.payment_profile.events.length > 0 && (
            <>
              <UI.HR />
              <UI.Div>
                <UI.Strong>{t('payment_profile_used_by')}</UI.Strong>
                <UI.UL>
                  {profile.payment_profile.events.map((event) => (
                    <UI.LI key={event.id}>
                      <UI.Link to={`/${project.id}/events/${event.id}/settings/payments`}>
                        {event.title}
                      </UI.Link>
                    </UI.LI>
                  ))}
                </UI.UL>
              </UI.Div>
            </>
          )}
          <UI.HR />
          {profile.payment_methods.map((paymentMethod) => (
            <MolliePaymentMethodSection
              connection={connection}
              profile={profile}
              paymentMethod={paymentMethod}
              key={paymentMethod.id}
            />
          ))}
        </>
      )}
    </UI.GridContainer>
  );
};

interface MolliePaymentMethodSectionProps {
  connection: MollieConnection;
  profile: MollieProfile;
  paymentMethod: MolliePaymentMethod;
}

const MolliePaymentMethodSection = ({ connection, profile, paymentMethod }: MolliePaymentMethodSectionProps) => {
  const { formatCurrency } = useLocale();

  const hasManagedFees = connection.mollie_app.pricing_type === MollieAppPricingType.managed_fees;

  const paymentMethodPricing = connection.payment_methods.find((item) => item.name === paymentMethod.payment_method_id);

  return (
    <UI.GridContainer sc={{ columns: '55px 1fr 55px' }}>
      <UI.Div>
        <img
          src={paymentMethod.image}
          alt={paymentMethod.description}
          width={55}
        />
      </UI.Div>
      <UI.Div>
        <UI.FlexContainer>
          <UI.H4>
            {paymentMethod.description}
            {paymentMethod.info && (
              <>
                {' '}
                <InfoTooltip>
                  <UI.H3 sc={{ mb: 2 }}>
                    {paymentMethod.description}
                  </UI.H3>
                  {paymentMethod.info}
                </InfoTooltip>
              </>
            )}
          </UI.H4>
        </UI.FlexContainer>

        {hasManagedFees && paymentMethodPricing && (
          <UI.Div>
            {paymentMethodPricing.fixed_fee > 0 && formatCurrency(paymentMethodPricing.fixed_fee)}
            {paymentMethodPricing.fixed_fee > 0 && paymentMethodPricing.variable_fee > 0 && ' + '}
            {paymentMethodPricing.variable_fee > 0 && `${paymentMethodPricing.variable_fee}%`}
          </UI.Div>
        )}

        {!hasManagedFees && paymentMethod.pricing.map((pricing, pricingIndex) => (
          <UI.Div key={pricingIndex}>
            {`${pricing.description}: `}
            {pricing.fixed > 0 && formatCurrency(pricing.fixed)}
            {pricing.fixed > 0 && pricing.variable !== '0' && ' + '}
            {pricing.variable !== '0' && `${pricing.variable}%`}
          </UI.Div>
        ))}
      </UI.Div>
      <UI.Div sc={{ textAlign: 'right' }}>
        <MolliePaymentMethodToggle profile={profile} paymentMethod={paymentMethod} />
      </UI.Div>
    </UI.GridContainer>
  );
};

interface MolliePaymentMethodToggleProps {
  profile: MollieProfile;
  paymentMethod: MolliePaymentMethod;
}

const MolliePaymentMethodToggle = ({ profile, paymentMethod }: MolliePaymentMethodToggleProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [continueUrl, setContinueUrl] = useState(null);

  const [openModal, closeModal] = useModal(() => (
    <UI.Modal>
      <UI.Paragraph>
        <Trans
          i18nKey="enable_mollie_payment_method_description"
          values={{ paymentMethod: paymentMethod.description }}
        >
          <UI.Strong>{paymentMethod.description}</UI.Strong>
        </Trans>
      </UI.Paragraph>
      <UI.Div sc={{ textAlign: 'right' }}>
        <UI.Button onClick={() => { closeModal(); }}>{t('close')}</UI.Button>
        {' '}
        <UI.AButton
          href={continueUrl}
          target="_blank"
          rel="noopener noreferrer"
          sc={{ brand: 'secondary' }}
        >
          {t('enable_in_mollie')}
          {' '}
          <UI.Icon>
            <ExternalLink />
          </UI.Icon>
        </UI.AButton>
      </UI.Div>
    </UI.Modal>
  ), [paymentMethod, continueUrl]);

  const [
    enableMolliePaymentMethod, { loading: enabling },
  ] = useMutation(EnableMolliePaymentMethodMutation, {
    variables: {
      input: {
        mollie_profile: {
          id: profile.id,
        },
        payment_method_id: paymentMethod.payment_method_id,
      },
    },
    onCompleted: ({ enableMolliePaymentMethod: result }) => {
      if (result.success) {
        notifier.success(t('notifications.payment_method_enabled'));
      } else if (result.continue_url) {
        setContinueUrl(result.continue_url);
        openModal();
      } else {
        notifier.error(t('notifications.something_went_wrong'));
      }
    },
  });

  const [
    disableMolliePaymentMethod, { loading: disabling },
  ] = useMutation(DisableMolliePaymentMethodMutation, {
    variables: {
      input: {
        mollie_profile: {
          id: profile.id,
        },
        payment_method_id: paymentMethod.payment_method_id,
      },
    },
    onCompleted: ({ disableMolliePaymentMethod: paymentMethod }) => {
      if (paymentMethod.enabled === false) {
        notifier.success(t('notifications.payment_method_disabled'));
      } else {
        notifier.error(t('notifications.something_went_wrong'));
      }
    },
  });

  const toggleMolliePaymentMethod = () => {
    if (paymentMethod.enabled) {
      disableMolliePaymentMethod();
    } else {
      enableMolliePaymentMethod();
    }
  };

  return (
    <UI.Toggle
      checked={paymentMethod.enabled}
      disabled={enabling || disabling}
      onChange={() => toggleMolliePaymentMethod()}
      aria-label={t(paymentMethod.enabled ? 'disable_option' : 'enable_option', { option: paymentMethod.description })}
    />
  );
};

export default MollieProfileSection;
